// ZeroKnowledge.jsx
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ── Icons ── */
const LockIcon = () => (
  <svg
    className="w-3.5 h-3.5 text-gray-500"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ServerIcon = () => (
  <svg
    className="w-[15px] h-[15px]"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const EyeIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ShieldIcon = () => (
  <svg
    className="w-4 h-4"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SparklesIcon = () => (
  <svg
    className="w-3 h-3"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 3L14 8L19 8L15 11L17 16L12 13L7 16L9 11L5 8L10 8L12 3Z" />
  </svg>
);

const YourViewContent = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="space-y-4"
    >
      <h2 className="text-white font-semibold text-lg leading-relaxed">
        Finalize Q3 Financial Projections for Board Meeting
      </h2>

      <motion.p
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-gray-300 text-sm leading-relaxed"
      >
        Discuss budget allocation and timeline. Ensure strict confidentiality
        regarding client details.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="flex items-center gap-3 pt-3"
      >
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-xs text-green-400">Active Session</span>
        </div>
        <div className="w-px h-3 bg-gray-700" />
        <div className="flex items-center gap-2">
          <ShieldIcon />
          <span className="text-xs text-gray-400">End-to-End Encrypted</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

const OurViewContent = () => {
  const [scrambledText, setScrambledText] = useState("");
  const [isDecoding, setIsDecoding] = useState(false);
  const originalText = "Finalize Q3 Financial Projections for Board Meeting";

  useEffect(() => {
    setIsDecoding(true);
    let iterations = 0;
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+{}[]|:;<>,.?/~";

    const interval = setInterval(() => {
      setScrambledText(
        originalText
          .split("")
          .map((char, index) => {
            if (index < iterations) return originalText[index];
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join(""),
      );

      iterations += 1.5;
      if (iterations >= originalText.length) {
        clearInterval(interval);
        setScrambledText(originalText);
        setTimeout(() => setIsDecoding(false), 500);
      }
    }, 30);

    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="space-y-4"
    >
      {/* Scrambled Title */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="relative"
      >
        <h2 className="text-white font-mono text-sm leading-relaxed break-all">
          {scrambledText || originalText}
        </h2>
        {isDecoding && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
        )}
      </motion.div>

      {/* Encrypted Data Block */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-black/60 rounded-lg p-4 relative overflow-hidden border border-purple-500/30"
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-purple-400 font-mono">
              ENCRYPTED PAYLOAD
            </span>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <SparklesIcon />
            </motion.div>
          </div>

          <div className="space-y-1 font-mono text-[11px]">
            {[
              "0x8F9A4C1B2E3D4F5A6B7C8D9E0F1A2B3C",
              "U2FsdGVkX1+Q5XH4z3xNv2v1u0c/s.n3.q+Po*N&m^l%k$j#i@h!",
              "aGVsbG8gdGhpcyBpcyBlbmNyeXB0ZWQgZGF0YQ==",
            ].map((line, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.1 }}
                className="text-blue-300 text-xs break-all"
              >
                {line}
              </motion.p>
            ))}
          </div>
        </div>

        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              "radial-gradient(circle at 20% 50%, rgba(139,92,246,0.1) 0%, transparent 50%)",
              "radial-gradient(circle at 80% 50%, rgba(139,92,246,0.1) 0%, transparent 50%)",
              "radial-gradient(circle at 20% 50%, rgba(139,92,246,0.1) 0%, transparent 50%)",
            ],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </motion.div>

      {/* Encryption Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-2 gap-2 pt-2"
      >
        {[
          { label: "Encryption", value: "AES-256-GCM" },
          { label: "Key Exchange", value: "ECDH" },
          { label: "Hash", value: "SHA-384" },
          { label: "Mode", value: "Galois/Counter" }
        ].map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 + i * 0.1 }}
            className="bg-black/40 rounded px-2 py-1"
          >
            <div className="text-[9px] text-gray-500">{item.label}</div>
            <div className="text-[10px] text-purple-300 font-mono">{item.value}</div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default function ZeroKnowledge() {
  const [activeView, setActiveView] = useState("your");
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section className="bg-gradient-to-br from-[#EAECF0] to-[#E5E8F0] flex flex-col items-center justify-center px-4 py-20 relative overflow-hidden">
      {/* Animated Background */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity, delay: 1 }}
        />
      </motion.div>

      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm
         border border-gray-200 rounded-full px-4 py-1.5 text-[13px] font-medium
          text-gray-600 mb-7 shadow-sm "
      >
        <LockIcon />
        <span className="ZeroKnowledge_badge">Zero-Knowledge Architecture</span>
      </motion.div>

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-center font-extrabold leading-[1.1] tracking-[-0.03em] mb-5 text-[clamp(35px,6vw,55px)]"
      >
        <span className="text-[#000]">Your Eyes Only. </span>
        <motion.span
          className="text-[#093a5b] inline-block"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Literally.
        </motion.span>
      </motion.h1>

      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="ZeroKnowledge_paragraph"
      >
        We engineered Simplifyte so your data is encrypted on your device before
        it ever reaches us. To anyone else—even our own engineers—your notes
        look like unbreakable cryptographic noise.
      </motion.p>

      {/* macOS Window */}
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.3, type: "spring", damping: 20 }}
        className="w-full max-w-[900px] bg-[#0D1117] rounded-[18px] overflow-hidden border border-white/[0.08] shadow-2xl"
        whileHover={{ scale: 1.01 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        {/* Title Bar */}
        <motion.div
          className="flex items-center justify-between flex-wrap gap-3 px-5 py-[14px] border-b border-white/[0.08] bg-gradient-to-r from-[#0D1117] to-[#0A0E12]"
          animate={{
            backgroundColor: isHovered
              ? "rgba(17, 24, 39, 0.8)"
              : "rgba(13, 17, 23, 1)",
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-4">
            {/* Traffic Lights */}
            <div className="flex gap-[6px]">
              {["#FF5F57", "#FFBD2E", "#28C840"].map((color, i) => (
                <motion.div
                  key={i}
                  className="w-3 h-3 rounded-full cursor-pointer"
                  style={{ backgroundColor: color }}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
            <div className="w-px h-4 bg-white/20" />

            {/* Server Info */}
            <motion.div
              className="flex items-center gap-[7px] text-[#9CA3AF] text-[13px]"
              animate={{ opacity: isHovered ? 1 : 0.8 }}
            >
              <ServerIcon />
              <span>in-ap-south-1 (Mumbai)</span>
            </motion.div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center bg-black/40 rounded-full p-1 backdrop-blur-sm">
            <motion.button
              onClick={() => setActiveView("your")}
              className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-300 cursor-pointer overflow-hidden ${
                activeView === "your"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-300"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {activeView === "your" && (
                <motion.div
                  className="absolute inset-0 bg-white rounded-full"
                  layoutId="activeTab"
                  transition={{ type: "spring", duration: 0.5 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-1.5">
                <EyeIcon />
                Your View
              </span>
            </motion.button>

            <motion.button
              onClick={() => setActiveView("our")}
              className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-300 cursor-pointer overflow-hidden ${
                activeView === "our"
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-300"
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {activeView === "our" && (
                <motion.div
                  className="absolute inset-0 bg-white rounded-full"
                  layoutId="activeTab"
                  transition={{ type: "spring", duration: 0.5 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-1.5">
                <EyeOffIcon />
                Our View
              </span>
            </motion.button>
          </div>
        </motion.div>

        {/* Window Body */}
        <div className="p-6 sm:p-8 md:p-12 relative">
          <motion.div
            className="bg-gradient-to-br from-[#0B1B2D] to-[#0A1622] border border-white/10 rounded-[18px] p-6 relative overflow-hidden shadow-xl"
            animate={{
              boxShadow:
                activeView === "our"
                  ? "0 0 30px rgba(139, 92, 246, 0.2)"
                  : "0 0 30px rgba(24, 119, 219, 0.1)",
            }}
            transition={{ duration: 0.5 }}
          >
            {/* Animated Gradient Background */}
            <motion.div
              className="absolute inset-0"
              animate={{
                background:
                  activeView === "our"
                    ? "radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 70%)"
                    : "radial-gradient(circle at 50% 50%, rgba(24, 119, 219, 0.1) 0%, transparent 70%)",
              }}
              transition={{ duration: 0.5 }}
            />

            {/* Content with Animated Transition */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeView}
                initial={{ opacity: 0, y: 20, rotateX: -10 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: -20, rotateX: 10 }}
                transition={{ duration: 0.4, type: "spring", damping: 25 }}
                className="relative z-10"
              >
                {activeView === "your" ? (
                  <YourViewContent />
                ) : (
                  <OurViewContent />
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* Bottom Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-6 text-center text-gray-400 text-xs flex items-center justify-center gap-2"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            >
              🔒
            </motion.div>
            <span>Data remains in India. Zero-knowledge verified.</span>
            <motion.div
              className="w-1 h-1 rounded-full bg-green-400"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full"
            style={{
              background: i % 2 === 0 ? "#3B82F6" : "#A855F7",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, (Math.random() - 0.5) * 50, 0],
              opacity: [0, 0.5, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>
    </section>
  );
}
